import type { PieChartAggregation, ViewConfigDimension } from '@salescore/core'
import { isNotNullish } from 'effect/Predicate'
import { useRecoilState } from 'recoil'

import { kpiParameterAtom } from '../recoil/navigation/atoms'

interface UsePieChartAggregationResult {
  aggregation: PieChartAggregation | undefined
  setBreakdown: (dimension: ViewConfigDimension | undefined) => void
  setCategory: (dimension: ViewConfigDimension | undefined) => void
}

export function usePieChartAggregation(): UsePieChartAggregationResult {
  const [kpiPivotParameter, setKpiPivotParameter] = useRecoilState(kpiParameterAtom)
  const { rows } = kpiPivotParameter.pivot
  const aggregation = rowsToPieAggregation(rows)

  const setBreakdown = (dimension: ViewConfigDimension | undefined): void => {
    setKpiPivotParameter((oldValue) => ({
      ...oldValue,
      pivot: {
        rows: [dimension, ...oldValue.pivot.rows.slice(1)].filter(isNotNullish),
        columns: [],
      },
    }))
  }

  const setCategory = (dimensionCategory: ViewConfigDimension | undefined): void => {
    setKpiPivotParameter((oldValue) => ({
      ...oldValue,
      pivot: {
        rows: [oldValue.pivot.rows[0], dimensionCategory].filter(isNotNullish),
        columns: [],
      },
    }))
  }

  return { aggregation, setBreakdown, setCategory }
}

export function rowsToPieAggregation(rows: readonly ViewConfigDimension[]): PieChartAggregation | undefined {
  const breakdown = rows[0]
  if (breakdown === undefined) return undefined
  return { chartFormat: 'pie', breakdown, category: rows[1] }
}

export function pieAggregationToRows(aggregation: PieChartAggregation): ViewConfigDimension[] {
  const rows: ViewConfigDimension[] = [aggregation.breakdown]
  if (aggregation.category !== undefined) rows.push(aggregation.category)
  return rows
}
